// external
import merge from "lodash/merge";
import { createApp } from "vue";
import { createI18n } from "vue-i18n";
import VueVirtualScroller from "vue-virtual-scroller";
import "vue-virtual-scroller/dist/vue-virtual-scroller.css";
import { Socket } from "vue-loading-spinner";
import Notifications from "@kyvg/vue3-notification";
import VueSafeHTML from "vue-safe-html";

import { luxonify } from "@teaming/common-util";
import { lang as commonEnumLang } from "@teaming/common-enums";
import { lang as commonUiLang } from "@teaming/common-ui";
import { lang as commonValidationLang } from "@teaming/common-validation";
import { lang as commonTemplateLang } from "@teaming/common-templates";
import { lang as commonGOKRLang } from "@teaming/common-gokr";
import { lang as commonTeamLang } from "@teaming/common-team";

import App from "../App";
import { initFontAwesome } from "./fontawesome";
import { initPrimeVue } from "./primevue";
import initializeStore from "../store";
import teamingWebTranslations from "../lang";
import authPluginFactory from "./authPlugin";
import AppConfirmationDialogHOC from "../components/AppConfirmationDialogHOC";
import NavSidebarLayout from "../layouts/NavSidebar/NavSidebarLayout";

/*
	Vue initialization should happen here and not bleed into main.js
*/
export function initializeVue( { config, authService, } ) {
	const i18n = createI18n( {
		messages: merge(
			commonEnumLang,
			commonUiLang,
			commonGOKRLang,
			commonTeamLang,
			commonValidationLang,
			commonTemplateLang,
			teamingWebTranslations
		),
	} );

	const app = createApp( App );
	initFontAwesome( { app, } );
	initPrimeVue( { app, } );
	const authPlugin = authPluginFactory( authService );

	const store = initializeStore( config );

	app
		.use( Notifications )
		.use( authPlugin )
		.use( store )
		.use( i18n )
		.use( VueVirtualScroller )
		.use( VueSafeHTML, { allowedTags: [ "a", ], } )
	;

	app.component( "AppConfirmationDialogHOC", AppConfirmationDialogHOC );
	app.component( "NavSidebarLayout", NavSidebarLayout );
	app.component( "Socket", Socket );

	// console support in templates, mostly for debugging
	app.use( {
		// eslint-disable-next-line no-unused-vars
		install( _app, options ) {
			_app.config.globalProperties.$console = console;
		},
	} );

	// NOTE: this allows us to start using luxon format strings in components
	// without requiring higher order components to pass luxon objects via props
	// TODO: remove once moment has been completely removed
	app.use( {
		// eslint-disable-next-line no-unused-vars
		install( _app, options ) {
			_app.config.globalProperties.$luxonify = luxonify;
		},
	} );

	// Auto-unwrap objects that are injected, such that we do not have to
	// access them like: `userProfile.value` any longer; this will be the
	// default behavior in future versions of vue3.
	app.config.unwrapInjectedRef = true;

	return { app, i18n, store, };
}
